import React, { forwardRef } from "react";
import { UPLOAD_URL } from "../../utils/constants";

export type VisitingCardTheme = {
  id: string;
  name: string;
  variant: "split" | "overlay" | "minimal" | "badge" | "diagonal";
  colors: {
    background: string;
    text: string;
    accent: string;
    secondary?: string;
    gradient?: string;
    border?: string;
    pillBg?: string;
    pillText?: string;
  };
};

type Props = {
  bureauInfo: any;
  theme: VisitingCardTheme;
};

const DynamicVisitingCard = forwardRef<HTMLDivElement, Props>(
  ({ bureauInfo, theme }, ref) => {
    const {
      bureauName,
      ownerName,
      mobileNumber,
      email,
      bureauAddress,
      address,
      location,
      about,
      navbarLogo,
    } = bureauInfo || {};

    const logoLetter = (bureauName?.trim()?.charAt(0) || "M").toUpperCase();

    const LogoBadge = () => (
      <div className="absolute top-3 right-5 w-11 flex items-center justify-center shadow-lg border-2 border-white bg-white overflow-hidden">
        {navbarLogo ? (
          <img
            src={`${UPLOAD_URL}/bureaus/navbar_logos/${navbarLogo}`}
            alt="Bureau Logo"
            className="w-full h-full object-cover"
          />
        ) : (
          <span
            className="text-white font-semibold"
            style={{
              background:
                "linear-gradient(135deg, rgba(124,58,237,1), rgba(236,72,153,1))",
            }}
          >
            {logoLetter}
          </span>
        )}
      </div>
    );

    const displayAddress = bureauAddress || address || location || "";
    const contactBlock = (
      <div className="text-xs space-y-1 leading-tight">
        {displayAddress && (
          <p className="flex items-start gap-1">
            <span>📍</span>
            <span>{displayAddress}</span>
          </p>
        )}
        {email && (
          <p className="flex items-center gap-1">
            <span>✉️</span>
            <span>{email}</span>
          </p>
        )}
        {mobileNumber && (
          <p className="flex items-center gap-1">
            <span>📞</span>
            <span>{mobileNumber}</span>
          </p>
        )}
      </div>
    );

    if (theme.variant === "split") {
      return (
        <div
          ref={ref}
          className="relative w-[360px] h-[210px] rounded-2xl shadow-xl overflow-hidden flex"
          style={{ background: theme.colors.background }}
        >
          <LogoBadge />
          <div
            className="w-2/5 p-4 text-white flex flex-col justify-between"
            style={{ background: theme.colors.gradient || theme.colors.accent }}
          >
            <p className="text-sm font-semibold uppercase tracking-wide">
              {bureauName || "Marriage Bureau"}
            </p>
            <p className="text-xs opacity-80 mt-1">
              {about || "Trusted matches, happy families"}
            </p>
          </div>
          <div
            className="flex-1 p-4 flex flex-col justify-between"
            style={{ color: theme.colors.text }}
          >
            <div>
              <p className="text-xl font-semibold">
                {ownerName || bureauName || "MeeMarriage Bureau"}
              </p>
              <p
                className="text-xs font-medium mt-1 uppercase tracking-wide"
                style={{ color: theme.colors.accent }}
              >
                Managing Director
              </p>
            </div>
            {contactBlock}
          </div>
        </div>
      );
    }

    if (theme.variant === "overlay") {
      return (
        <div
          ref={ref}
          className="relative w-[360px] h-[210px] rounded-2xl shadow-xl p-5 overflow-hidden"
          style={{
            background: theme.colors.gradient || theme.colors.background,
          }}
        >
          <LogoBadge />
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#fff,transparent_55%)]" />
          <div className="relative h-full flex flex-col justify-between text-white">
            <div>
              <p className="text-sm uppercase tracking-widest opacity-80">
                {bureauName || "Marriage Bureau"}
              </p>
              <p className="text-2xl font-bold mt-1">
                {ownerName || bureauName || "MeeMarriage"}
              </p>
            </div>
            <div className="text-sm space-y-1">{contactBlock}</div>
          </div>
        </div>
      );
    }

    if (theme.variant === "minimal") {
      return (
        <div
          ref={ref}
          className="relative w-[360px] h-[210px] rounded-2xl shadow-lg p-5 flex flex-col justify-between border-2"
          style={{
            background: theme.colors.background,
            borderColor: theme.colors.border || theme.colors.accent,
            color: theme.colors.text,
          }}
        >
          <LogoBadge />
          <div>
            <p
              className="text-xs uppercase tracking-widest font-semibold"
              style={{ color: theme.colors.accent }}
            >
              {bureauName || "MeeMarriage Bureau"}
            </p>
            <p className="text-2xl font-bold mt-2">
              {ownerName || "Your Name"}
            </p>
            <p className="text-xs mt-1 opacity-80">Founder & Counselor</p>
          </div>
          <div className="flex items-center justify-start">{contactBlock}</div>
        </div>
      );
    }

    if (theme.variant === "badge") {
      return (
        <div
          ref={ref}
          className="relative w-[360px] h-[210px] rounded-2xl shadow-xl p-5 flex flex-col justify-between text-white"
          style={{ background: theme.colors.background }}
        >
          <LogoBadge />
          <div>
            <p className="text-xs uppercase tracking-[0.3em] opacity-80">
              {bureauName || "MeeMarriage"}
            </p>
            <p className="text-2xl font-semibold mt-1">
              {ownerName || "Your Name"}
            </p>
          </div>
          <div
            className="rounded-xl p-4 text-sm"
            style={{ background: theme.colors.secondary || "#ffffff20" }}
          >
            {contactBlock}
          </div>
        </div>
      );
    }

    // diagonal / fallback
    return (
      <div
        ref={ref}
        className="relative w-[360px] h-[210px] rounded-2xl shadow-xl overflow-hidden text-white"
        style={{ background: theme.colors.background }}
      >
        <LogoBadge />
        <div
          className="absolute inset-0"
          style={{
            background: theme.colors.gradient || theme.colors.accent,
            clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
            opacity: 0.9,
          }}
        />
        <div className="absolute inset-0 p-5 flex flex-col justify-between">
          <div>
            <p className="text-sm uppercase tracking-widest opacity-80">
              {bureauName || "MeeMarriage Bureau"}
            </p>
            <p className="text-2xl font-bold mt-1">
              {ownerName || "Your Name"}
            </p>
          </div>
          <div className="text-sm">{contactBlock}</div>
        </div>
      </div>
    );
  },
);

DynamicVisitingCard.displayName = "DynamicVisitingCard";

export const VISITING_CARD_THEMES: VisitingCardTheme[] = [
  {
    id: "aurora-split",
    name: "Aurora Split",
    variant: "split",
    colors: {
      background: "#fdf4ff",
      text: "#111827",
      accent: "#a855f7",
      gradient: "linear-gradient(135deg, #7c3aed, #ec4899)",
    },
  },
  {
    id: "sunset-overlay",
    name: "Sunset Glow",
    variant: "overlay",
    colors: {
      background: "#fb7185",
      text: "#ffffff",
      accent: "#fb7185",
      gradient: "linear-gradient(135deg, #f97316, #ec4899)",
    },
  },
  {
    id: "emerald-minimal",
    name: "Emerald Edge",
    variant: "minimal",
    colors: {
      background: "#f0fdf4",
      text: "#065f46",
      accent: "#10b981",
      border: "#34d399",
    },
  },
  {
    id: "royal-badge",
    name: "Royal Badge",
    variant: "badge",
    colors: {
      background: "linear-gradient(135deg, #312e81, #1e1b4b)",
      text: "#ffffff",
      accent: "#a5b4fc",
      secondary: "#4338ca99",
    },
  },
  {
    id: "crimson-diagonal",
    name: "Crimson Diagonal",
    variant: "diagonal",
    colors: {
      background: "#111827",
      text: "#ffffff",
      accent: "linear-gradient(135deg, #ef4444, #f97316)",
    },
  },
  {
    id: "ocean-split",
    name: "Ocean Split",
    variant: "split",
    colors: {
      background: "#e0f2fe",
      text: "#0c4a6e",
      accent: "#0284c7",
      gradient: "linear-gradient(135deg, #0ea5e9, #10b981)",
    },
  },
  {
    id: "violet-overlay",
    name: "Violet Bloom",
    variant: "overlay",
    colors: {
      background: "#7c3aed",
      text: "#ffffff",
      accent: "#f5d0fe",
      gradient: "linear-gradient(120deg, #7c3aed, #c026d3)",
    },
  },
  {
    id: "sunrise-badge",
    name: "Sunrise Badge",
    variant: "badge",
    colors: {
      background: "linear-gradient(135deg, #f97316, #ea580c)",
      text: "#fff7ed",
      accent: "#fed7aa",
      secondary: "#ffffff1f",
    },
  },
  {
    id: "jade-diagonal",
    name: "Jade Strike",
    variant: "diagonal",
    colors: {
      background: "#052e16",
      text: "#f0fdf4",
      accent: "linear-gradient(135deg, #22c55e, #14b8a6)",
    },
  },
  {
    id: "blush-split",
    name: "Blush Split",
    variant: "split",
    colors: {
      background: "#fff1f2",
      text: "#831843",
      accent: "#db2777",
      gradient: "linear-gradient(135deg, #f472b6, #db2777)",
    },
  },
  {
    id: "azure-overlay",
    name: "Azure Mist",
    variant: "overlay",
    colors: {
      background: "#0ea5e9",
      text: "#e0f2fe",
      accent: "#bae6fd",
      gradient: "linear-gradient(135deg, #2563eb, #0ea5e9)",
    },
  },
  {
    id: "sandstone-minimal",
    name: "Sandstone",
    variant: "minimal",
    colors: {
      background: "#fef3c7",
      text: "#92400e",
      accent: "#f97316",
      border: "#facc15",
    },
  },
  {
    id: "noir-badge",
    name: "Noir Crest",
    variant: "badge",
    colors: {
      background: "linear-gradient(135deg, #0f172a, #1e293b)",
      text: "#e2e8f0",
      accent: "#38bdf8",
      secondary: "#1e293b",
    },
  },
  {
    id: "copper-diagonal",
    name: "Copper Edge",
    variant: "diagonal",
    colors: {
      background: "#3f1f0f",
      text: "#fff7ed",
      accent: "linear-gradient(135deg, #f97316, #fbbf24)",
    },
  },
  {
    id: "teal-split",
    name: "Teal Split",
    variant: "split",
    colors: {
      background: "#ecfeff",
      text: "#115e59",
      accent: "#14b8a6",
      gradient: "linear-gradient(135deg, #0d9488, #06b6d4)",
    },
  },
  {
    id: "berry-overlay",
    name: "Berry Shine",
    variant: "overlay",
    colors: {
      background: "#be185d",
      text: "#fff1f2",
      accent: "#fbcfe8",
      gradient: "linear-gradient(135deg, #db2777, #be185d)",
    },
  },
  {
    id: "slate-minimal",
    name: "Slate Border",
    variant: "minimal",
    colors: {
      background: "#e2e8f0",
      text: "#1e293b",
      accent: "#475569",
      border: "#94a3b8",
    },
  },
  {
    id: "forest-badge",
    name: "Forest Crest",
    variant: "badge",
    colors: {
      background: "linear-gradient(135deg, #166534, #15803d)",
      text: "#dcfce7",
      accent: "#bbf7d0",
      secondary: "#15803d",
    },
  },
  {
    id: "plum-diagonal",
    name: "Plum Diagonal",
    variant: "diagonal",
    colors: {
      background: "#581c87",
      text: "#fdf4ff",
      accent: "linear-gradient(135deg, #9333ea, #a21caf)",
    },
  },
  {
    id: "graphite-split",
    name: "Graphite Split",
    variant: "split",
    colors: {
      background: "#f5f5f4",
      text: "#292524",
      accent: "#57534e",
      gradient: "linear-gradient(135deg, #0f172a, #475569)",
    },
  },
];

export default DynamicVisitingCard;
